import {
  type BuildFeeViewModel,
  type PaymentViewModel,
  type Plan,
  type SubscribeViewModel,
} from "@lumora/core";
import { PackagesSection, useViewModel } from "@lumora/ui";
import { BuildFeeSection } from "./BuildFeeSection.js";
import { PaymentForm } from "./PaymentForm.js";
import { StripeProvider } from "./StripeProvider.js";

export interface NewCustomerSectionProps {
  subscribeVM: SubscribeViewModel;
  paymentVM: PaymentViewModel;
  buildFeeVM: BuildFeeViewModel;
  publishableKey: string | undefined;
  returnUrl: string;
  /** DB-backed hosting plans (prices from platform_stripe_prices via API). */
  hostingPlans: readonly Plan[];
}

/**
 * NewCustomerSection is the "New customer" path: pick a plan (PackagesSection
 * drives SubscribeViewModel.start → clientSecret), then the Stripe Payment
 * Element appears for autopay, plus the one-off build-fee invoice.
 */
export function NewCustomerSection({
  subscribeVM,
  paymentVM,
  buildFeeVM,
  publishableKey,
  returnUrl,
  hostingPlans,
}: NewCustomerSectionProps) {
  const subscribeState = useViewModel(subscribeVM);

  return (
    <div data-lumora-view="new-customer">
      {/* Backend-priced plans only: Foundation has no Stripe price, so subscribing 400s (#450). */}
      <PackagesSection
        className="site-packages"
        viewModel={subscribeVM}
        plans={hostingPlans}
        title="Choose a plan"
      />

      {buildFeeVM.hasQuotedFee ? <BuildFeeSection viewModel={buildFeeVM} /> : null}

      {subscribeState.clientSecret ? (
        <section className="portal-section" data-lumora-view="autopay">
          <StripeProvider publishableKey={publishableKey} clientSecret={subscribeState.clientSecret}>
            <PaymentForm viewModel={paymentVM} returnUrl={returnUrl} />
          </StripeProvider>
        </section>
      ) : subscribeVM.isActiveWithoutPayment ? (
        <section className="portal-section" data-lumora-view="autopay-active">
          <p data-lumora-subscribe-active className="portal-notice">
            Plan active — no card needed for this plan.
          </p>
        </section>
      ) : null}
    </div>
  );
}
